import React from "react";

type SkeletonProps = Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> & {
  /** Width — number is px, string passes through (e.g. '60%'). Defaults to full width. */
  width?: number | string;
  /** Height in px. Defaults to the current line height, so inline use keeps the row height. */
  height?: number | string;
  /** Pill-shaped rather than the default small radius. */
  pill?: boolean;
};

function toCssSize(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === "number" ? `${value}px` : value;
}

/**
 * Placeholder bar shown where a value will land once its query resolves.
 *
 * A skeleton is not decoration — it is the "unknown" state of a datum, and it
 * exists so unknown never renders as a plausible-looking zero, an empty list,
 * or a status the user might act on. Size it like the value it stands in for
 * so nothing reflows when the real number arrives.
 *
 * Only render this on a cold load (`loading && !data`). On a background
 * refetch the previous value is still true, and swapping it for a shimmer
 * makes polled surfaces strobe.
 *
 * Marked `aria-hidden` — the enclosing region carries `aria-busy`, so
 * assistive tech announces "busy" once instead of once per bar.
 */
export function Skeleton({
  width,
  height,
  pill,
  className,
  style,
  ...rest
}: SkeletonProps) {
  const cls = ["tb-skeleton", pill ? "tb-skeleton-pill" : "", className]
    .filter(Boolean)
    .join(" ");
  return (
    <span
      className={cls}
      aria-hidden="true"
      style={{ width: toCssSize(width), height: toCssSize(height), ...style }}
      {...rest}
    />
  );
}

type SkeletonTextProps = {
  /** Number of lines to draw. */
  lines?: number;
  /** Width of the last line — real paragraphs don't end flush. */
  lastLineWidth?: number | string;
  className?: string;
};

/** Multi-line variant for prose blocks (captions, descriptions, notes). */
export function SkeletonText({
  lines = 3,
  lastLineWidth = "60%",
  className,
}: SkeletonTextProps) {
  return (
    <span
      className={["tb-skeleton-text", className].filter(Boolean).join(" ")}
      aria-hidden="true"
    >
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} width={i === lines - 1 ? lastLineWidth : "100%"} />
      ))}
    </span>
  );
}
